import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { PostFeeGroupsDTO, useGetCoinsQuery } from '../../../../../api';
import { Merchant } from '../../../../../types/models/merchant.model';
import { PostMerchantDto } from '../../../../../api/controllers/merchant';

type CreateMerchantCoinConfigModalProps = {
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
  fields: PostMerchantDto['body'] | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  isLoading: boolean;
};

export const CreateMerchantCoinConfigModal: FC<CreateMerchantCoinConfigModalProps> = (props) => {
  const { open, onClose, onCreate, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // api
  const { data: coinList, isLoading: isCoinsLoading } = useGetCoinsQuery();

  // validation for required fields
  const requiredFields = [
    'min_amount',
    'coin_id',
    'ttl_seconds',
  ] as (keyof PostMerchantDto['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || !fields[field]) {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  const optionSelectOptions = coinList?.coins.map((it) => {
    return (
      <MenuItem key={it.id} value={it.id}>
        {it.name}
      </MenuItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('profile-merchant-coin-config.modals.create.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="coin_id"
            value={fields?.coin_id ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-merchant-coin-config.modals.create.fields.coin-name')}
            fullWidth
          >
            {optionSelectOptions || []}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="ttl_seconds"
            value={fields?.ttl_seconds ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-merchant-coin-config.modals.create.fields.ttl-seconds')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="min_amount"
            value={fields?.min_amount ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('profile-merchant-coin-config.modals.create.fields.min-amount')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isCoinsLoading || isLoading || checkSubmitDisabling()}
        >
          {t('profile-merchant-coin-config.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('profile-merchant-coin-config.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
